import {
  Button,
  Form,
  Select,
  Stack,
  Text,
  Textarea,
  useBasisForm,
} from "basis";
import { DatePicker } from "components/DatePicker/DatePicker";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { add } from "date-fns";
import { usePageTracking } from "react-event-tracker";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import { primaryReasonOptions, secondaryReasonOptions } from "../options";
import { getIsShortForm, getNumberOfSteps } from "../reducer/hardshipSelectors";
import { ActionTypes, ReasonsForApplyingState } from "../reducer/types";
import { validatePaymentStartDate } from "./reasonsForApplyingForm";

export const ReasonsForApplying = () => {
  const { state, dispatch } = useHardshipApplicationContext();
  const { navigate, goBack } = useNav();

  const numberOfSteps = getNumberOfSteps(state);
  const { reasonsForApplying } = state;

  const { methods, Field, CustomField } = useBasisForm<ReasonsForApplyingState>(
    {
      defaultValues: reasonsForApplying,
    },
  );

  const onSubmit = (values: ReasonsForApplyingState) => {
    dispatch({ type: ActionTypes.COMPLETE_REASONS_FOR_APPLYING, ...values });
    if (getIsShortForm(state)) {
      navigate("review");
    } else {
      navigate("liabilities");
    }
  };

  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "hardship",
      siteSubSection: "reasons-for-applying",
      pageTitle: "Your reasons for applying",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8" width="100%" width-sm="400">
            <Stack gap="4">
              <Text>
                <strong>Step 3 of {numberOfSteps}</strong>
              </Text>
              <Text as="h2" textStyle="heading4">
                Your reasons for applying
              </Text>
              <Text color="grey.t75">
                <strong>Tell us why you need Hardship Care.</strong>
              </Text>
            </Stack>

            <Field
              label="Main reason"
              name="primaryReason"
              options={primaryReasonOptions}
              as={Select}
            />
            <Field
              label="Other reason"
              name="secondaryReason"
              options={secondaryReasonOptions}
              as={Select}
            />
            <CustomField
              defaultValue={{
                day: "",
                month: "",
                year: "",
              }}
              name="startDate"
              validate={validatePaymentStartDate}
            >
              {(props) => (
                <DatePicker
                  label="If your application is approved, when is the earliest you can start making payments?"
                  {...props}
                  fromDate={new Date()}
                  toDate={add(new Date(), { days: 30 })}
                  helpText="We’ll schedule your first repayment in line with your next regular monthly repayment date that falls after this."
                  error={
                    typeof props.error === "string" ? props.error : undefined
                  }
                />
              )}
            </CustomField>
            <Field
              label="Anything else you’d like to tell us about your situation?"
              name="situation"
              validate={(value: string) =>
                !value
                  ? "It doesn’t have to be long, just provide a sentence to describe your current situation."
                  : null
              }
              as={Textarea}
            />
            <FormActions>
              <FormActions.Primary width="172">
                <Button variant="primary" type="submit">
                  Next
                </Button>
              </FormActions.Primary>
              <FormActions.Secondary width="172">
                <Button variant="secondary" onClick={goBack}>
                  Back
                </Button>
              </FormActions.Secondary>
            </FormActions>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
